import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import useAsyncFn from 'react-use/lib/useAsyncFn';

import {
  Notification,

} from '@internal/plugin-components-react';

import {
  Box,
  Button,
  Card,
  CardContent,
  FilledInput,
  FormHelperText,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@material-ui/core';

import { useReleaseTagFormStyles } from './style';
import { useApi } from '@backstage/core-plugin-api';
import { deploymentManagementApiRef } from '../../api/types';






const DEFAULT_FORM_VALUES = {
  major: '',
  minor: '',
  patch: '',
};



/**
 * getDefaultFormValues - Generates FormValues based on a version tag.
 *
 * @param {string | undefined} tag - The version tag (e.g., "1.2.3") to parse and extract major, minor, and patch values.
 * @returns {FormValues} - An object representing major, minor, and patch values.
 */
const getDefaultFormValues = (tag) => {
  if (!tag) {
    return DEFAULT_FORM_VALUES;
  }

  const [major, minor, patch] = tag.split('.').map(part => part.trim());

  return {
    major: major || '',
    minor: minor || '',
    patch: patch || '',
  };
};

const numericRegex = /^[0-9]+$/;

export default function ReleaseTagForm({ releaseLabel, tag }) {
  const classes = useReleaseTagFormStyles();
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(
    getDefaultFormValues(tag),
  );
  const deploymentManagementApi = useApi(deploymentManagementApiRef);
  const { releaseId } = useParams();
  const [notificationProps, setNotificationProps] = useState(
    { isOpen: false, message: '' },
  );
  const [queryUrl] = useSearchParams();
  const modeQueryUrl = queryUrl.get('mode');
  const isEditMode = modeQueryUrl === 'edit';
  const isFormValuesEmpty = Object.values(formValues).every(
    value => value === '',
  );
  //  Helper text / error message is not displayed if form values are empty
  //  because a user can make all form values empty and click the save button to delete the tag
  const isHelperTextDisplayed =
    !isFormValuesEmpty &&
    Object.values(formValues).some(value => !numericRegex.test(value));

  const [
    { loading: isUpdatingReleaseTag, error: errorUpdatingReleaseTag },
    updateReleaseTag,
  ] = useAsyncFn(async (tag, releaseId) => {
    const response = await deploymentManagementApi.updateReleaseTag(releaseId, {
      tag,
    });

    setNotificationProps({
      isOpen: true,
      message: 'Changes has been saved',
      status: 'success',
      autoclose: true,
      handleOnClose: () => setNotificationProps({ isOpen: false, message: '' }),
    });

    return response;
  }, []);

  useEffect(() => {
    if (errorUpdatingReleaseTag) {
      setNotificationProps({
        isOpen: true,
        message: errorUpdatingReleaseTag.message,
        status: 'error',
        autoclose: true,
        handleOnClose: () =>
          setNotificationProps({ isOpen: false, message: '' }),
      });
    }
  }, [errorUpdatingReleaseTag]);

  const onClearFormValues = () => {
    setFormValues(DEFAULT_FORM_VALUES);
  };

  const onNavigateBack = () => {
    navigate('/deployment-management/release-history');
  };

  const onSubmitTag = () => {
    const tag = isFormValuesEmpty
      ? ''
      : `${formValues.major}.${formValues.minor}.${formValues.patch}`;
    updateReleaseTag(tag, releaseId);
  };

  const handleInput

 = e => {
    const inputValue = e.target.value;
    const inputName = e.target.name;

    setFormValues(prevFormValues => ({
      ...prevFormValues,
      [inputName]: inputValue,
    }));
  };

  const checkErrorInput = (formValue) => {
    //  Error input is not displayed if form values are empty
    //  because a user can make all form values empty and click the save button to delete the tag
    if (!isFormValuesEmpty && !numericRegex.test(formValue)) {
      return true;
    }

    return false;
  };

  return (
    React.createElement(React.Fragment, null
      , React.createElement(Typography, { variant: "h5",}
        , isEditMode ? 'Edit' : 'Add', " AGS tag version"
      )
      , React.createElement(Card, null
        , React.createElement(CardContent, null
          , React.createElement(InputLabel, { className: classes.inputLabel, htmlFor: "release-id",}, "ID"

          )
          , React.createElement(FilledInput, {
            fullWidth: true,
            className: classes.filledInput,
            id: "release-id",
            value: releaseId,
            disabled: true,}
          )
          , React.createElement(InputLabel, { className: classes.inputLabel, htmlFor: "release-name",}, "Release Name"

          )
          , React.createElement(FilledInput, {
            fullWidth: true,
            className: classes.filledInput,
            id: "release-name",
            value: releaseLabel || '',
            disabled: true,}
          )

          , React.createElement(InputLabel, { className: classes.inputLabel, htmlFor: "release-version",}, "AGS Tag Version"

          )
          , React.createElement(Box, { display: "flex", gridColumnGap: 8, alignItems: "flex-end",}
            /* 
            Turns out, when users type characters like "e," ",", and ".", the onChange event won't trigger. 
            To ensure users can only input numbers from 0 to 99, we make the following adjustments: 
            1. Change the input type to "text" so that the onChange event triggers for every character.
            2. Watch user input and display an error message if the user inputs the wrong format.
            
            Here's a reference for this behavior: 
            https://stackoverflow.com/questions/68357012/onchange-is-not-called-when-typing-e-in-number-input-in-react-and-vanilla-java 
            */
            , React.createElement(OutlinedInput, {
              type: "text",
              name: "major",
              className: classes.numberInput,
              value: formValues.major || '',
              onChange: handleInput,
              error: checkErrorInput(formValues.major),}
            ), "."

            , React.createElement(OutlinedInput, {
              type: "text",
              name: "minor",
              className: classes.numberInput,
              value: formValues.minor || '',
              onChange: handleInput,
              error: checkErrorInput(formValues.minor),}
            ), "."

            , React.createElement(OutlinedInput, {
              type: "text",
              name: "patch",
              className: classes.numberInput,
              value: formValues.patch || '',
              onChange: handleInput,
              error: checkErrorInput(formValues.patch),}
            )
          )

          , React.createElement(FormHelperText, { className: classes.helperText,}
            , isHelperTextDisplayed &&
              'Please enter the valid semantic version with X.Y.Z format number, example: 1.1.0'
          )

          , React.createElement(Box, {
            display: "flex",
            gridColumnGap: 4,
            justifyContent: "flex-end",
            marginTop: 6,}
          
            , isEditMode && (
              React.createElement(Button, {
                variant: "text",
                onClick: onClearFormValues,
                disabled: isUpdatingReleaseTag,
                className: classes.clearTagButton,}
              , "Clear Tag"

              )
            )
            , React.createElement(Button, {
              variant: "outlined",
              color: "primary",
              onClick: onNavigateBack,
              disabled: isUpdatingReleaseTag,}
            , "Back"

            )
            , React.createElement(Button, {
              variant: "contained",
              color: "primary",
              onClick: onSubmitTag,
              // Need to disable the save button until the user updates the input
              // and the helper text/error text is gone
              disabled: isUpdatingReleaseTag || isHelperTextDisplayed,}
            , "Save"

            )
          )
        )
      )

      , React.createElement(Notification, { ...notificationProps, className: classes.notification,} )
    )
  );
}
